import React, {useCallback, useEffect, useRef, useState} from 'react';
import {selectors as bookSelectors} from '../../../Ducks/books';
import {selectors as accountSelectors} from '../../../Ducks/account';
import {connect} from 'react-redux';
import GridElement from '../../Common/GridElement';
import {
    Badge,
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import {toast} from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import BookOrderFormModal from './BookOrderFormModal';
import {fetchGet, fetchPut} from '../../../API/util';
import {getPendingOrdersCount} from '../../../API/books';
import {NOT_DEFINED, statusOptions} from '../../Utils/constants';
import CapabilityChecker from "../../Common/CapabilityChecker";
import moment from "moment";
import {RemoveRedEye} from "@mui/icons-material";

const ORDER_COLUMNS = [
    {key: 'book_order_id', label: 'Book Order ID', disableFilters: false, sortable: true},
    {key: 'order_number', label: 'Order Number', disableFilters: false, sortable: true},
    {key: 'purchase_id', label: 'Purchase ID', disableFilters: false, sortable: true},
    {key: 'person_name', label: 'Person Name', disableFilters: false, sortable: true},
    {key: 'product_name', label: 'Product Name', disableFilters: false, sortable: true},
    {key: 'delivery_country', label: 'Delivery Country', disableFilters: false, sortable: true},
    {key: 'internal_notes', label: 'Internal Notes', disableFilters: false, sortable: true},
    {key: 'status', label: 'Status', disableFilters: false, sortable: true},
    {key: 'created_at', label: 'Created At', disableFilters: false, sortable: true},
];
const makeHeader = (title) => {
    const parts = title.split('_');
    return parts.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const makeAccessor = (key) => key;

const parseRow = (raw) => {
    const item = {...raw};
    Object.keys(item).forEach((k) => {
        if (k === "dispatched_at" || k === "created_at" && item[k] != "") {
            item[k] = moment(item[k], "DD/MM/YYYY HH:mm").format("DD/MM/YYYY");
        }
    });
    item.product_name = item?.product.product_name || NOT_DEFINED;
    item.id = item.book_order_id;
    item.order_id = item.book_order_id;
    return item;
};

const BookOrders = ({
                        mutating,
                        user
                    }) => {
    const [refreshKey, setRefreshKey] = useState(0);
    const prevMutating = useRef(mutating);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [rowsCount, setRowsCount] = useState(0);
    const [filters, setFilters] = useState([]);
    const [controlledPageIndex, setControlledPageIndex] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [pendingCount, setPendingCount] = useState(0);
    const fetchIdRef = useRef(0);
    const [viewOnly, setViewOnly] = useState(false);
    // Fetch count of pending orders
    const fetchPendingCount = useCallback(async () => {
        try {
            const payload = await getPendingOrdersCount();
            const total = payload?.pagination?.total ?? 0;
            setPendingCount(total);
        } catch (error) {
            setPendingCount(0);
        }
    }, []);

    // API call to update order
    const updateOrder = async ({id, obj}) => {
        try {
            const updated = await fetchPut(`/book-orders/${id}`, obj);
            setData((prev) =>
                prev.map((order) =>
                    order.book_order_id === id ? {...order, ...obj} : order
                )
            );
            return updated;
        } catch (error) {
            throw new Error(error.message || 'Failed to update order');
        }
    };

    const handleSave = async (orderId, payload) => {
        try {
            await updateOrder({
                id: orderId,
                obj: {
                    recipient_name: payload.recipient_name,
                    delivery_address: payload.delivery_address.street,
                    delivery_city: payload.delivery_address.city,
                    delivery_postal_code: payload.delivery_address.postal_code,
                    delivery_country: payload.delivery_address.country,
                    vendor_name: payload.vendors,
                    contact_phone: payload.recipient_contact.phone_number,
                    contact_email: payload.recipient_contact.email_address,
                    internal_notes: payload.notes,
                    status: payload.status,
                    books: payload.books,
                    order_number: payload.order_number
                }
            });
            setModalOpen(false);
            setSelectedOrder(null);
            toast.success(`Order ${orderId} updated successfully`);
        } catch (error) {
            toast.error(error.message);
        }
    };

    const renderActionsCell = (row) => {
        return (
            <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                {row.status === statusOptions.pending_review &&
                    <CapabilityChecker capabilities={['can_review_book_orders', 'can_place_book_orders']}>
                        <Tooltip title="Edit Order">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedOrder(row);
                                    setModalOpen(true);
                                }}
                            >
                                <EditIcon fontSize="inherit"/>
                            </IconButton>
                        </Tooltip>
                    </CapabilityChecker>
                }
                {(row.status === statusOptions.approved || row.status === statusOptions.order) &&
                    <CapabilityChecker capabilities={['can_review_book_orders']}>
                        <Tooltip title="Edit Order">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedOrder(row);
                                    setModalOpen(true);
                                }}
                            >
                                <EditIcon fontSize="inherit"/>
                            </IconButton>
                        </Tooltip>
                    </CapabilityChecker>
                }
                {(row.status === statusOptions.cancelled || row.status === statusOptions.delivered) &&
                    <Tooltip title="Order Details">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrder(row);
                                setModalOpen(true);
                                setViewOnly(true);
                            }}
                        >
                            <RemoveRedEye fontSize="inherit"/>
                        </IconButton>
                    </Tooltip>
                }
            </div>
        );
    };

    const renderStatusCell = (row) => {
        let reviewBookOrdersCapability = false;

        if (user) {
            let user_capabilities = user.user_details.capabilities.map((item) => item.capability_title)

            if (user_capabilities.includes('can_review_book_orders')) {
                reviewBookOrdersCapability = true;
            } else {
                reviewBookOrdersCapability = false;
            }
        }

        const status = row.status || statusOptions.pending_review;

        return (
            <FormControl size="small" sx={{minWidth: 120}}>
                <InputLabel>Status</InputLabel>
                <Select
                    value={status}
                    label="Status"
                    disabled={(status != statusOptions.pending_review && !reviewBookOrdersCapability) || status == statusOptions.delivered || status == statusOptions.cancelled}
                    onChange={(e) => {
                        const newStatus = e.target.value;
                        setData((prev) =>
                            prev.map((order) =>
                                order.book_order_id === row.book_order_id
                                    ? {...order, status: newStatus}
                                    : order
                            )
                        );
                        updateOrder({id: row.book_order_id, obj: {status: newStatus}})
                            .then(() => {
                                fetchPendingCount()
                                toast.success(`Order ${row.book_order_id} status updated to ${newStatus}`);
                            })
                            .catch((err) => {
                                toast.error(err.message);
                                setData((prev) =>
                                    prev.map((order) =>
                                        order.book_order_id === row.book_order_id
                                            ? {...order, status: row.status}
                                            : order
                                    )
                                );
                            });
                    }}
                >
                    {Object.values(statusOptions).map((status) => (
                        <MenuItem key={status} value={status}>
                            {status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </MenuItem>
                    ))}

                </Select>
            </FormControl>
        );
    };

    const [cols] = useState([
        ...ORDER_COLUMNS.map((item) => {
            if (item.key === 'status') {
                return {
                    column: item.key,
                    accessor: makeAccessor(item.key),
                    minWidth: 150,
                    Header: makeHeader(item.label),
                    filter: 'fuzzyText',
                    Cell: ({row}) => renderStatusCell(row.original),
                };
            }
            if (item.key === 'product_name') {
                return {
                    column: item.key,
                    accessor: makeAccessor(item.key),
                    minWidth: 150,
                    Header: makeHeader(item.label),
                    filter: 'fuzzyText',
                };
            }
            return {
                column: item.key,
                accessor: makeAccessor(item.key),
                minWidth: 150,
                Header: makeHeader(item.label),
                disableFilters: item.disableFilters,
                filter: 'fuzzyText',
            };
        }),
        {
            column: '__actions',
            accessor: '__actions',
            Header: 'Actions',
            minWidth: 120,
            disableSortBy: true,
            filter: null,
            localOnly: true,
            disableFilters: true,
            Cell: ({row}) => renderActionsCell(row.original),
        },
    ]);

    const buildFilterQuery = (filtersArg, columnsArg) => {
        let query = '';
        filtersArg.forEach((item) => {
            const colMeta = columnsArg.find((c) => c.accessor === item.id);
            const source = colMeta?.column || item.id;
            query += `&contains[${source}]=${encodeURIComponent(item.value)}`;
        });
        return query;
    };

    const buildSortQuery = (sortBy, columnsArg) => {
        if (!sortBy?.length) return '&sortBy=created_at&order=desc';
        const sb = sortBy[0];
        const colMeta = columnsArg.find((c) => c.accessor === sb.id);
        const source = colMeta?.column || sb.id;
        return `&sortBy=${sb.desc ? '-' : ''}${source}`;
    };

    const normalizeResponse = (payload, pageSize) => {
        const d = payload?.data;
        if (!d) return {rows: [], total: 0, pageCount: 0};
        const rows = Array.isArray(d) ? d.map(parseRow) : [];
        const total = (payload?.pagination?.total ?? rows.length) ?? 0;
        const pageCount = (Math.ceil(total / (pageSize || 50)) ?? 0);
        return {rows, total, pageCount};
    };

    const fetchData = useCallback(async ({pageSize, pageIndex, sortBy, filters: f, columns: c}) => {
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        try {
            const filterQuery = buildFilterQuery(f, c);
            const sortQuery = buildSortQuery(sortBy, c);
            const url = `/book-orders?results=${pageSize}&offset=${pageIndex * pageSize}${sortQuery}${filterQuery}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`;
            const payload = await fetchGet(url);
            if (fetchId !== fetchIdRef.current) return;
            const {rows, total, pageCount} = normalizeResponse(payload, pageSize);
            setData(rows);
            setRowsCount(total);
            setPageCount(pageCount);
        } catch (error) {
            toast.error(error.message || 'Failed to fetch orders');
        } finally {
            setLoading(false);
        }
    }, [searchQuery]);

    const resetFilters = useCallback(() => setFilters([]), []);

    useEffect(() => {
        if (prevMutating.current && !mutating) {
            setRefreshKey((k) => k + 1);
        }
        prevMutating.current = mutating;
    }, [mutating]);

    useEffect(() => {
        fetchPendingCount();
    }, [fetchPendingCount, refreshKey]);

    useEffect(() => {
        setRefreshKey((k) => k + 1);
    }, [searchQuery]);

    return (
        <>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                    <h2 className="page-title" style={{margin: 0, fontSize: '1.8rem'}}>Book Order Management</h2>
                    <Badge
                        badgeContent={pendingCount}
                        color="primary"
                        showZero
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '0.85rem',
                                padding: '4px 8px',
                                minWidth: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                transform: 'translate(-50%, -50%)',
                            },
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 500,
                                color: 'text.secondary',
                                padding: '4px 8px',
                                backgroundColor: 'grey.100',
                                borderRadius: '4px',
                            }}
                        >
                            Pending
                        </Typography>
                    </Badge>
                    <Tooltip title="Refresh Pending Count">
                        <IconButton
                            size="small"
                            onClick={() => fetchPendingCount()}
                            sx={{color: 'primary.main'}}
                        >
                            <RefreshIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Box>
                <TextField
                    label="Search Orders"
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                    }}
                    placeholder="Search by Order ID, Order Number, Opportunity ID, Person, or Book Title"
                    sx={{width: 400}}
                />
            </Box>

            <GridElement
                key={refreshKey}
                columns={cols}
                data={data}
                fetchData={fetchData}
                loading={loading}
                pageCount={pageCount}
                controlledPageIndex={controlledPageIndex}
                setControlledPageIndex={setControlledPageIndex}
                filters={filters}
                setFilters={setFilters}
                resetFilters={resetFilters}
                rowsCount={rowsCount}
                handleRowClick={() => {
                }}
                hideSelection
                initialSortBy="book_order_id"
            />

            <BookOrderFormModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedOrder(null);
                    setViewOnly(false);
                }}
                initialValues={selectedOrder}
                viewOnly={viewOnly}
                onSave={handleSave}
                isSaving={loading}
            />
        </>
    );
};

const mapStateToProps = (state) => ({
    user: accountSelectors.user(state),
    mutating: bookSelectors.isLoading(state),
});

export default connect(mapStateToProps, {})(BookOrders);